<script setup lang="ts">
type Tone = 'default' | 'muted' | 'success' | 'warning' | 'danger' | 'info';

withDefaults(
  defineProps<{
    tone?: Tone;
    unstyled?: boolean;
  }>(),
  {
    tone: 'default',
    unstyled: false
  }
);
</script>

<template>
  <span
    v-bind="$attrs"
    :class="unstyled ? undefined : ['ui-badge', `ui-badge--${tone}`]"
  >
    <slot />
  </span>
</template>

<style scoped>
.ui-badge {
  display: inline-flex;
  min-height: 1.625rem;
  align-items: center;
  gap: var(--ui-space-1);
  border: 1px solid transparent;
  border-radius: var(--ui-radius-full);
  box-shadow: inset 0 1px 0 color-mix(in srgb, var(--ui-foreground-inverse) 24%, transparent);
  font-size: 0.6875rem;
  font-weight: 760;
  letter-spacing: 0.075em;
  line-height: 1.1;
  padding: 0.32rem 0.72rem;
  text-transform: uppercase;
  white-space: nowrap;
}

.ui-badge :deep(svg),
.ui-badge :slotted(svg) {
  width: 0.875rem;
  height: 0.875rem;
  flex: 0 0 auto;
}

.ui-badge--default {
  border-color: var(--ui-border);
  background: linear-gradient(180deg, var(--ui-card-bg), var(--ui-card-bg-muted));
  color: var(--ui-foreground);
}

.ui-badge--muted {
  border-color: color-mix(in srgb, var(--ui-border) 84%, transparent);
  background: var(--ui-card-bg-muted);
  color: var(--ui-foreground-muted);
}

.ui-badge--success {
  border-color: var(--ui-status-success-border);
  background: linear-gradient(180deg, color-mix(in srgb, var(--ui-success) 12%, var(--ui-card-bg)), var(--ui-status-success-bg));
  color: var(--ui-status-success-text);
}

.ui-badge--warning {
  border-color: var(--ui-status-warning-border);
  background: linear-gradient(180deg, color-mix(in srgb, var(--ui-warning) 13%, var(--ui-card-bg)), var(--ui-status-warning-bg));
  color: var(--ui-status-warning-text);
}

.ui-badge--danger {
  border-color: var(--ui-status-danger-border);
  background: linear-gradient(180deg, color-mix(in srgb, var(--ui-danger) 12%, var(--ui-card-bg)), var(--ui-status-danger-bg));
  color: var(--ui-status-danger-text);
}

.ui-badge--info {
  border-color: var(--ui-status-info-border);
  background: linear-gradient(180deg, color-mix(in srgb, var(--ui-info) 12%, var(--ui-card-bg)), var(--ui-status-info-bg));
  color: var(--ui-status-info-text);
}
</style>
